/* eslint-disable no-use-before-define */
import Component from '@bythepixel/component-loader';
import formatCurrency from '@Core/utility/formatCurrency';
import getNumericCharactersOnlyAndConvertToInt from '@Core/utility/getNumericCharactersOnlyAndConvertToInt';
import { formatCurrencyInput } from '@Utility';

export default class FinancingCalculator extends Component {
  static selector = '[data-comp-financing-calculator]';

  constructor(...args) {
    super(...args);
    const { selector } = FinancingCalculator;
    const identifier = selector.substring(1, selector.length - 1); // Removes brackets from selector
    this.aprEl = this.$container.querySelector(`[${identifier}="apr"]`);
    this.customRangeSliderEl = this.$container.querySelector(`[${identifier}="custom-input-range"]`);
    this.downPaymentEl = this.$container.querySelector(`[${identifier}="down-payment"]`);
    this.interestRateEl = this.$container.querySelector(`[${identifier}="interest-rate"]`);
    this.livingQuartersEl = this.$container.querySelector(`[${identifier}="living-quarters"]`);
    this.monthlyPaymentEl = this.$container.querySelector(`[${identifier}="monthly-payment"]`);
    this.payoffAmountEl = this.$container.querySelector(`[${identifier}="payoff-amount"]`);
    this.percentageEl = this.$container.querySelector(`[${identifier}="percentage-indicator"]`);
    this.priceEl = this.$container.querySelector(`[${identifier}="price"]`);
    this.rangeSliderEl = this.$container.querySelector(`[${identifier}="percentage"]`);
    this.termEl = this.$container.querySelector(`[${identifier}="term"]`);
    this.tradeInEl = this.$container.querySelector(`[${identifier}="trade-in"]`);
    this.initialPageLoad = true;

    if (this.aprEl || this.interestRateEl) this.apr = this.getAprPercentage();
    if (this.rangeSliderEl) this.initListeners();
    if (this.livingQuartersEl) this.initLivingQuarterListeners();
  }

  calculateDownPayment() {
    const percentageInput = this.percentageEl.value;
    const priceInput = this.priceEl.value;

    if (percentageInput !== '') {
      const percentage = getNumericCharactersOnlyAndConvertToInt(percentageInput);
      const price = priceInput !== '$' && priceInput !== '' ? getNumericCharactersOnlyAndConvertToInt(priceInput) : 0;
      let downPayment = (price * percentage) / 100;
      downPayment = Number.isNaN(downPayment) ? '$' : downPayment;

      if (downPayment === '$') {
        this.downPaymentEl.innerText = '$';
      } else {
        // downPayment % 1 === 0 checks to see if it is a whole number
        this.downPaymentEl.innerText = downPayment % 1 === 0 ? formatCurrency(downPayment) : formatCurrencyWithDecimals(downPayment);
      }
    }
  }

  calculateMonthlyPayment() {
    // remainingBalance = price - downPayment - (tradeInValue - payoffAmount)
    // numerator = remainingBalance * (apr / 12)
    // denominator = 1 - (1 + (apr / 12)^-(termInMonths))
    // monthlyPayment = numerator / denominator
    // ref: https://www.thecalculatorsite.com/finance/calculators/carloancalculator.php
    const priceInput = this.priceEl.value;
    const downPaymentInput = this.downPaymentEl.value;
    const tradeInInput = this.tradeInEl.value;
    const payoffAmountInput = this.payoffAmountEl.value;
    const termInput = this.termEl.value;

    if (downPaymentInput !== '' && termInput !== '') {
      const price = priceInput !== '$' && priceInput !== '' ? getNumericCharactersOnlyAndConvertToInt(priceInput) : 0;
      const downPayment = getNumericCharactersAndDecimalAndConvertToFloat(downPaymentInput);
      const payoffAmount = payoffAmountInput !== '$' && payoffAmountInput !== ''
        ? getNumericCharactersOnlyAndConvertToInt(payoffAmountInput) : 0;
      let tradeIn = tradeInInput !== '$' && tradeInInput !== '' ? getNumericCharactersOnlyAndConvertToInt(tradeInInput) : 0;

      // Payoff amount is subtracted from the trade-in value
      // Ex: A person may have a trade-in valued at $10,000, however they could still owe $8,000 on it
      // so only $2,000 should be applied to the balance.
      tradeIn = payoffAmount >= tradeIn ? 0 : tradeIn - payoffAmount;
      const term = getNumericCharactersOnlyAndConvertToInt(termInput);

      const remainingBalance = price - downPayment - tradeIn;
      const numerator = remainingBalance <= 0 ? 0 : remainingBalance * (this.apr / 12);
      const denominator = 1 - ((1 + (this.apr / 12)) ** -term);
      let monthlyPayment = numerator / denominator;
      monthlyPayment = Number.isNaN(monthlyPayment) ? 0 : monthlyPayment;

      // monthlyPayment % 1 === 0 checks to see if it is a whole number
      this.monthlyPaymentEl.innerText = monthlyPayment % 1 === 0 ? formatCurrency(monthlyPayment)
        : formatCurrencyWithDecimals(monthlyPayment);
    }
  }

  formatInterestRateInput() {
    let { value } = this.interestRateEl;

    value = String(value).replace(/[^0-9.%]/g, '');
    this.interestRateEl.value = value;
  }

  getAprPercentage() {
    let apr;

    // If an interest rate input field exists, get the APR percentage from that instead
    if (this.interestRateEl) {
      apr = this.interestRateEl.value.replace(/%/g, '');
    } else {
      apr = this.aprEl.innerText.replace(/%/g, '');
    }

    return parseFloat(apr).toFixed(2) / 100;
  }

  getInputRangeHandlePosition() {
    const {
      scrollWidth,
      max,
      min,
      value,
    } = this.rangeSliderEl;

    // Hack for input range default value
    // A limitation of input ranges is that we are not able to set a default value since the
    // default value of the range slider is (maxValue - minValue) / 2
    let rangeValue = value;
    if (this.initialPageLoad) {
      this.rangeSliderEl.value = 15;
      rangeValue = this.rangeSliderEl.value;
    }

    const outputPosition = rangeValue / max;
    const offset = (scrollWidth * outputPosition) / max;
    return Number(((rangeValue - min - offset) * max) / (max - min));
  }

  handleLivingQuarterEvents() {
    const livingQuarterOptions = [
      120,
      132,
      144,
      180,
      192,
      204,
      216,
      228,
      240,
    ];

    const noLivingQuarterOptions = [
      60,
      72,
      84,
      120,
      144,
    ];

    const { value } = this.livingQuartersEl;

    // Reset term dropdown options
    if (this.termEl.length !== 1) {
      for (let i = this.termEl.length - 1; i >= 1; i -= 1) {
        this.termEl.options[i] = undefined;
      }
    }

    if (value === 'Yes') {
      this.populateTermsDropdown(livingQuarterOptions);
    } else if (value === 'No') {
      this.populateTermsDropdown(noLivingQuarterOptions);
    }

    this.calculateMonthlyPayment();
  }

  initListeners() {
    this.setPercentageOutputPosition(); // Needs to come before the calculations or it will cause a race condition bug
    this.calculateDownPayment();
    this.calculateMonthlyPayment();
    this.setDownPaymentOutputPosition();
    formatCurrencyInput(this.priceEl);
    this.initialPageLoad = false;

    // Only the trucks financing page has an interest rate input field
    if (this.interestRateEl) {
      this.interestRateEl.addEventListener('input', () => {
        this.apr = this.getAprPercentage();
        this.calculateMonthlyPayment();
        this.formatInterestRateInput();
      });

      this.interestRateEl.addEventListener('blur', () => {
        formatPercentageInput(this.interestRateEl);
      });
    }

    this.payoffAmountEl.addEventListener('input', () => {
      this.calculateMonthlyPayment();
      formatCurrencyInput(this.payoffAmountEl);
    });

    this.priceEl.addEventListener('input', () => {
      this.calculateDownPayment();
      this.calculateMonthlyPayment();
      this.setDownPaymentOutputPosition();
      formatCurrencyInput(this.priceEl);
    });

    this.rangeSliderEl.addEventListener('input', () => {
      this.setPercentageOutputPosition(); // Needs to come before the calculations or it will cause a race condition bug
      this.calculateDownPayment();
      this.calculateMonthlyPayment();
      this.setDownPaymentOutputPosition();
    });

    this.termEl.addEventListener('change', () => {
      this.calculateDownPayment();
      this.calculateMonthlyPayment();
    });

    this.tradeInEl.addEventListener('input', () => {
      this.calculateMonthlyPayment();
      formatCurrencyInput(this.tradeInEl);
    });
  }

  initLivingQuarterListeners() {
    this.livingQuartersEl.addEventListener('change', () => {
      this.handleLivingQuarterEvents();
    });
  }

  populateTermsDropdown(options) {
    options.forEach(option => {
      const newOption = document.createElement('option');
      newOption.value = option;
      newOption.innerText = `${option} months`;
      this.termEl.appendChild(newOption);
    });
  }

  setDownPaymentOutputPosition() {
    // Dividing by 16 converts pixels to rems
    // Dividing by 1.75 is the magic number I've been using to calculate the offset
    this.downPaymentOffset = ((this.downPaymentEl.clientWidth - this.percentageEl.clientWidth) / 16) / 1.75;
    this.downPaymentEl.style.left = `calc(${this.getInputRangeHandlePosition()}% - ${this.downPaymentOffset}rem)`;
  }

  setPercentageOutputPosition() {
    const newVal = this.getInputRangeHandlePosition();

    this.customRangeSliderEl.style.setProperty('--progressPercent', `calc(${newVal}% + 0.5rem)`);
    this.percentageEl.innerText = `${this.rangeSliderEl.value}%`;
    this.percentageEl.style.left = `${newVal}%`;
  }
}

function formatCurrencyWithDecimals(value) {
  // toLocaleString adds commas and formats it to have 2 decimals
  return `$${value.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })}`;
}

function formatPercentageInput(input) {
  let { value } = input;

  value = String(value).replace(/%/g, '');

  // eslint-disable-next-line no-param-reassign
  input.value = `${parseFloat(value).toFixed(2)}%`;
}

function getNumericCharactersAndDecimalAndConvertToFloat(value) {
  /* eslint-disable */
  value = String(value).replace('$', '');
  value = String(value).replace(/,/g, '');
  /* eslint-enable */

  return parseFloat(value).toFixed(2);
}
